import type { EventItem } from '@/types';
import { CONFIG } from '@/config';
import { store } from '@/state';
import { showToast } from './toast';

/** Event feed element reference */
let eventFeedElement: HTMLElement | null = null;

/**
 * Initialize event feed module
 */
export function initEventFeed(): void {
  eventFeedElement = document.getElementById('event-feed');

  if (!eventFeedElement) {
    console.warn('Event feed element not found');
    return;
  }

  // Pause auto-scroll on hover
  eventFeedElement.addEventListener('mouseenter', () => {
    store.set('eventFeedAutoScroll', false);
    eventFeedElement?.classList.remove('auto-scroll');
  });

  eventFeedElement.addEventListener('mouseleave', () => {
    store.set('eventFeedAutoScroll', true);
    eventFeedElement?.classList.add('auto-scroll');
  });
}

/**
 * Add an event to the feed (syslog-style single line)
 * @param event - Event item to add
 */
export function addEventToFeed(event: EventItem): void {
  const feed = eventFeedElement ?? document.getElementById('event-feed');
  if (!feed) return;

  const item = document.createElement('div');
  item.className = `event-line ${event.severity}`;

  const time = new Date().toLocaleTimeString('en-US', { hour12: false });

  // Build syslog-style single line
  let line = `<span class="ev-time">${time}</span>`;
  line += `<span class="ev-severity">${event.severity.toUpperCase()}</span>`;
  line += `<span class="ev-source">${escapeHtml(event.source)}</span>`;
  line += `<span class="ev-msg">${escapeHtml(event.message)}</span>`;

  if (event.fiveTuple) {
    const ft = event.fiveTuple;
    line += `<span class="ev-tuple">${escapeHtml(ft.srcIp)}:${ft.srcPort} &rarr; ${escapeHtml(ft.dstIp)}:${ft.dstPort} ${escapeHtml(ft.protocol)}</span>`;
  }

  if (event.mbps !== undefined) {
    line += `<span class="ev-bw">${event.mbps.toFixed(1)}Mbps</span>`;
  }

  if (event.threat !== undefined) {
    line += `<span class="ev-threat" style="color:${getThreatColor(event.threat)}">T${event.threat}</span>`;
  }

  if (event.meta && !event.fiveTuple) {
    line += `<span class="ev-meta">${escapeHtml(event.meta)}</span>`;
  }

  item.innerHTML = line;

  // Click to filter related flows
  item.addEventListener('click', () => {
    showToast(`Filtering: ${event.source}`);
  });

  // Insert at top (newest first, scrolls down)
  feed.insertBefore(item, feed.firstChild);

  // Limit feed size
  while (feed.children.length > CONFIG.maxEventFeedItems) {
    feed.removeChild(feed.lastChild!);
  }

  // Auto-scroll to top if enabled
  if (store.get('eventFeedAutoScroll')) {
    feed.scrollTop = 0;
  }
}

/**
 * Clear all events from the feed
 */
export function clearEventFeed(): void {
  const feed = eventFeedElement ?? document.getElementById('event-feed');
  if (feed) {
    feed.innerHTML = '';
  }
}

/**
 * Get threat level color
 */
function getThreatColor(threat: number): string {
  const colors: Record<number, string> = {
    0: 'rgb(0, 255, 150)',
    1: 'rgb(150, 255, 0)',
    2: 'rgb(255, 255, 0)',
    3: 'rgb(255, 165, 0)',
    4: 'rgb(255, 50, 0)',
    5: 'rgb(255, 0, 50)',
  };
  return colors[threat] ?? 'rgb(200, 200, 200)';
}

/**
 * Escape HTML special characters
 * @param text - Text to escape
 * @returns Escaped text
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
